import type { GetArAgingReportResponse } from '@vy/api-definition';
import { OUTSTANDING_BREAKDOWN_OPTIONS } from '@vy/api-definition';
import type { ItemOf } from '@vy/utils';
import { groupBy, sumBy } from '@vy/utils';

type SummaryBreakdownItem = ItemOf<GetArAgingReportResponse['summaryBreakdown']>;

function filterByDaysOutstanding(
  invoices: SummaryBreakdownItem[],
  min: number,
  max: number,
): SummaryBreakdownItem[] {
  return invoices.filter((item) => {
    return item.daysOutstanding > min && item.daysOutstanding <= max;
  });
}

export function buildSummaryFromBreakdown(
  summaryBreakdown: GetArAgingReportResponse['summaryBreakdown'],
): GetArAgingReportResponse['summary'] {
  const invoicesByCustomer = groupBy(summaryBreakdown, (item) => item.customer.id);
  const entries = Array.from(invoicesByCustomer.entries());

  return entries.map(([, customerInvoices]) => {
    const { customer } = customerInvoices[0];

    const upToOneMonth = filterByDaysOutstanding(customerInvoices, 0, 30);
    const oneToTwoMonths = filterByDaysOutstanding(customerInvoices, 30, 60);
    const twoToThreeMonths = filterByDaysOutstanding(customerInvoices, 60, 90);
    const moreThanThreeMonths = filterByDaysOutstanding(customerInvoices, 90, Infinity);

    return {
      customer: { id: customer.id, name: customer.name },
      outstandingBreakdown: {
        [OUTSTANDING_BREAKDOWN_OPTIONS.UP_TO_ONE_MONTH]: {
          grossAmount: sumBy(upToOneMonth, (invoice) => invoice.remainingAmount),
          invoicesCount: upToOneMonth.length,
        },
        [OUTSTANDING_BREAKDOWN_OPTIONS.ONE_TO_TWO_MONTHS]: {
          grossAmount: sumBy(oneToTwoMonths, (invoice) => invoice.remainingAmount),
          invoicesCount: oneToTwoMonths.length,
        },
        [OUTSTANDING_BREAKDOWN_OPTIONS.TWO_TO_THREE_MONTHS]: {
          grossAmount: sumBy(twoToThreeMonths, (invoice) => invoice.remainingAmount),
          invoicesCount: twoToThreeMonths.length,
        },
        [OUTSTANDING_BREAKDOWN_OPTIONS.MORE_THAN_THREE_MONTHS]: {
          grossAmount: sumBy(moreThanThreeMonths, (invoice) => invoice.remainingAmount),
          invoicesCount: moreThanThreeMonths.length,
        },
      },
    };
  });
}
